"use client";

import React from "react";
import { useRouter } from "next/navigation";
import { Header, type HeaderProps } from "@/components/header";
import { Logo } from "@/components/ui/logo";
import { AppConfig } from "@/config/app-config";
import type { AppNotification } from "@/components/header";

// GNB 전용 인터페이스
interface GNBProps
  extends Omit<
    HeaderProps,
    | "user"
    | "onUserLogout"
    | "onUserProfileClick"
    | "onUserSettingsClick"
    | "onHelpClick"
    | "onViewAllNotifications"
    | "showHelp"
    | "showSearch"
    | "showNotifications"
    | "notificationCount"
  > {
  // 사용자 정보 간소화
  userName?: string;
  userEmail?: string;
  userRole?: string;
  userAvatar?: string;

  // 네비게이션 관련
  onSearch?: (query: string) => void;
  onNotificationClick?: (notification: AppNotification) => void;

  // 페이지 이동 경로 설정
  profilePath?: string;
  settingsPath?: string;
  helpPath?: string;
  notificationsPath?: string;
  loginPath?: string;

  // 커스텀 핸들러 (기본 동작 대신 사용)
  customLogoutHandler?: () => void;
  customProfileHandler?: () => void;
  customSettingsHandler?: () => void;
  customHelpHandler?: () => void;
  customNotificationsHandler?: () => void;

  // LNB 관련
  onCollapsedChange?: (collapsed: boolean) => void;
  collapsed?: boolean;
}

export function GNB({
  userName = "관리자",
  userEmail,
  userRole = "Administrator",
  userAvatar,
  profilePath = "/profile",
  settingsPath = "/settings",
  helpPath = "/help",
  notificationsPath = "/notifications",
  loginPath = "/login",
  customLogoutHandler,
  customProfileHandler,
  customSettingsHandler,
  customHelpHandler,
  customNotificationsHandler,
  onNotificationClick,
  onSearch,
  onCollapsedChange,
  collapsed = false,
  ...headerProps
}: GNBProps & {
  onCollapsedChange?: (collapsed: boolean) => void;
  collapsed?: boolean;
}) {
  const router = useRouter();

  // 기본 핸들러들
  const handleLogout = () => {
    if (customLogoutHandler) {
      customLogoutHandler();
    } else {
      // 기본 로그아웃 처리
      console.log("로그아웃 처리 중...");
      // 로컬 스토리지나 쿠키에서 인증 정보 제거
      localStorage.removeItem("auth_token");
      sessionStorage.clear();
      // 로그인 페이지로 리다이렉트
      router.push(loginPath);
    }
  };

  const handleProfile = () => {
    if (customProfileHandler) {
      customProfileHandler();
    } else {
      router.push(profilePath);
    }
  };

  const handleSettings = () => {
    if (customSettingsHandler) {
      customSettingsHandler();
    } else {
      router.push(settingsPath);
    }
  };

  const handleHelp = () => {
    if (customHelpHandler) {
      customHelpHandler();
    } else {
      router.push(helpPath);
    }
  };

  const handleViewAllNotifications = () => {
    if (customNotificationsHandler) {
      customNotificationsHandler();
    } else {
      router.push(notificationsPath);
    }
  };

  const handleNotificationClick = (notification: AppNotification) => {
    if (onNotificationClick) {
      onNotificationClick(notification);
    } else {
      // 기본 동작: 알림 상세 페이지로 이동
      router.push(`${notificationsPath}/${notification.id}`);
    }
  };

  const handleSearch = (query: string) => {
    if (onSearch) {
      onSearch(query);
    } else {
      // 기본 동작: 검색 페이지로 이동
      router.push(`/search?q=${encodeURIComponent(query)}`);
    }
  };

  return (
    <div className="w-full flex items-center h-16 px-4 border-b bg-background border-r">
      {/* GNB 맨 왼쪽: 로고+사이트명+LNB 접기(X) 버튼 */}
      <div className="flex items-center gap-2 mr-6">
        <Logo variant="icon" size="md" clickable={false} className="w-8 h-8" />
        <div className="flex flex-col">
          <span className="font-bold text-lg tracking-tight leading-none">WJPNCMA</span>
          <span className="text-[9px] text-muted-foreground font-medium opacity-70">ver {AppConfig.app.version}</span>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-end">
        <Header
          {...headerProps}
          // 사용자 정보는 LNB로 이동했으므로 user 속성 제거
          user={undefined}
          onViewAllNotifications={handleViewAllNotifications}
          onNotificationClick={handleNotificationClick}
          onSearch={handleSearch}
        />
      </div>
    </div>
  );
}

// 특정 사용자 타입별 사전 설정된 GNB 컴포넌트들
export function AdminGNB(props: Omit<GNBProps, "userRole">) {
  return <GNB {...props} userRole="관리자" />;
}

export function UserGNB(
  props: Omit<GNBProps, "userRole" | "showNotifications">
) {
  return <GNB {...props} userRole="사용자" />;
}

export function GuestGNB(
  props: Omit<GNBProps, "userName" | "userRole" | "showNotifications">
) {
  return <GNB {...props} userName="게스트" userRole="Guest" />;
}

// HOC: 페이지에 GNB를 자동으로 적용
export function withGNB<T extends object>(
  Component: React.ComponentType<T>,
  gnbProps: GNBProps = {}
) {
  return function WrappedComponent(props: T) {
    return (
      <div className="min-h-screen bg-background">
        <GNB {...gnbProps} />
        <Component {...props} />
      </div>
    );
  };
}
